import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Close } from "@dreamhub/designsystem/icons";
import { DrawerTitle as CustomDrawerTitle } from "../../FieldManagement/components/DrawerTitle";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { APIFieldItem } from "@/pages/settings/types";
import { useFieldForm } from "../../FieldManagement/hooks/useFieldForm";
import { FieldFormContent } from "../../FieldManagement/components/FieldFormContent";
import { FieldFormFooter } from "../../FieldManagement/components/FieldFormFooter";
import { IconButton } from "@/components/ui/icon-button";
import { WorkflowRequiredFieldSection } from "./components/WorkflowRequiredFieldSection";
import { AIAutofillSection } from "./components/AIAutofillSection";
import {
  buildFieldConfig,
  initializeFieldFormData
} from "../../FieldManagement/utils/fieldFormUtils";
import { DreamerFlow } from "./hooks/useAutofillApi";
import {
  SystemFieldReplacement,
  useSystemFieldReplacement
} from "./hooks/useSystemFieldReplacement";
import { AUTOFILL_TRANSLATION_KEY } from "./const";
import { workflowFieldHasEdits } from "./helpers";

const TRANSLATION_KEY_TABS = "settings.deals";
const TRANSLATION_PREFIX = "settings.deals.workflow.editGeneralField";

export interface WorkflowFieldSaveData {
  id: string;
  fieldLabel?: string;
  fieldType?: string;
  fieldHelpText?: string | null;
  isRequiredForPass?: boolean;
  config?: Record<string, unknown> | null;
  filledByDreamer?: boolean;
  dreamerFlow?: DreamerFlow | null;
  systemFieldReplacement?: SystemFieldReplacement | null;
}

interface EditGeneralWorkflowFieldDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  fieldData: APIFieldItem | null;
  questionnaireId?: string;
  enableAutofill?: boolean;
  onSave: () => void;
  // Required so a system-field rename can never fall back to an in-place edit — the
  // caller's save handler is what routes it through the replacement flow.
  onSaveField: (fieldData: WorkflowFieldSaveData) => void | Promise<void>;
  onDelete?: (fieldId: string) => void;
}

export const EditGeneralWorkflowFieldDrawer: React.FC<
  EditGeneralWorkflowFieldDrawerProps
> = ({
  isOpen,
  onOpenChange,
  fieldData,
  questionnaireId,
  enableAutofill = true,
  onDelete,
  onSave,
  onSaveField
}) => {
  const { t } = useTranslation();
  const showAutofill = enableAutofill;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRequiredForPass, setIsRequiredForPass] = useState(false);
  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);
  const [filledByDreamer, setFilledByDreamer] = useState(false);
  const [, setAutofillInstructions] = useState<string | null>(null);
  const [dreamerFlow, setDreamerFlow] = useState<DreamerFlow | null>(null);
  const [autofillSaveBlocked, setAutofillSaveBlocked] = useState(false);

  useEffect(() => {
    if (fieldData) {
      setIsRequiredForPass(fieldData.required || false);
      setFilledByDreamer(fieldData.filledByDreamer ?? false);
      setAutofillInstructions(null);
      setDreamerFlow(null);
      setAutofillSaveBlocked(false);
    }
  }, [fieldData]);

  const {
    formState,
    isFormValid,
    formRef,
    formSetValue,
    setFormSetValue,
    setShowHelpText,
    setShowInCreation,
    setIsRequired,
    handleFormValid,
    handleOptionsManagerChange,
    isSubmitting: isFieldFormSubmitting
  } = useFieldForm({
    fieldData,
    entityType: EntityType.DEAL,
    isEditMode: true
  });

  const isSubmitting = isFieldFormSubmitting;

  // The form takes its label a render after the field arrives, so fall back to the
  // field's own name until it does — otherwise an unedited field reads as renamed.
  const currentLabel = formState.formData.fieldLabel || fieldData?.label || "";
  const {
    promptOwner,
    onAutofillQuestionIdChange,
    replacement,
    discardDraft,
    markSaved
  } = useSystemFieldReplacement(fieldData, currentLabel);

  const handleClose = () => {
    discardDraft();
    onOpenChange(false);
  };

  const handleDeleteClick = () => {
    setIsDeleteConfirmed(false);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (fieldData) {
      onDelete?.(fieldData.id);
      setIsDeleteModalOpen(false);
      handleClose();
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleSave = async () => {
    try {
      const fieldFormData = formState.formData;

      // Marked before the handoff: a failed save may already have published the draft
      // as a live question (and the server may have moved answers onto it), so closing
      // after a failure must never delete it — a leaked hidden draft is recoverable,
      // a deleted question holding migrated answers is not.
      markSaved();
      await onSaveField({
        id: fieldData!.id,
        fieldLabel: fieldFormData.fieldLabel,
        fieldType: fieldFormData.fieldType,
        fieldHelpText: fieldFormData.fieldHelpText || null,
        isRequiredForPass,
        config: buildFieldConfig(
          formState.formData,
          formState.optionsManagerData,
          true,
          savedOptionIds
        ),
        filledByDreamer,
        dreamerFlow: filledByDreamer ? dreamerFlow : null,
        systemFieldReplacement: replacement
      });
      onSave();
      handleClose();
    } catch (error) {
      console.error("Failed to update workflow field:", error);
    }
  };

  const getTitle = () => {
    return <CustomDrawerTitle title={t(`${TRANSLATION_PREFIX}.title`)} />;
  };

  if (!fieldData) {
    return null;
  }

  const savedForm = initializeFieldFormData(fieldData);
  const savedOptionIds = (
    savedForm.optionsManagerData.optionItems ?? []
  ).flatMap(({ id }) => (id === undefined ? [] : [id]));
  const hasEdits = workflowFieldHasEdits(fieldData, {
    label: currentLabel,
    helpText: formState.formData.fieldHelpText,
    required: isRequiredForPass,
    filledByDreamer,
    instructions: dreamerFlow?.instructions ?? null,
    dreamerFlow,
    config: buildFieldConfig(
      formState.formData,
      formState.optionsManagerData,
      true,
      savedOptionIds
    ),
    savedConfig: buildFieldConfig(
      savedForm.formData,
      savedForm.optionsManagerData,
      true
    )
  });

  return (
    <>
      <Drawer
        open={isOpen}
        onOpenChange={onOpenChange}
        direction="right"
        modal={false}
        dismissible={false}
      >
        <AppDrawerContent isSettingsDrawer>
          <DrawerHeader>
            <DrawerTitle>
              {getTitle()}
            </DrawerTitle>
            <IconButton
              data-test="close-edit-general-workflow-field-drawer"
              onClick={handleClose}
              variant="iconNoBg"
              size="md"
            >
              <Close />
            </IconButton>
          </DrawerHeader>

          <div className="w-full flex-1 min-h-0 overflow-y-auto">
            <FieldFormContent
              formData={formState.formData}
              optionsManagerData={formState.optionsManagerData}
              fieldData={fieldData}
              formSetValue={formSetValue}
              showHelpText={formState.showHelpText}
              showInCreation={formState.showInCreation}
              isRequired={formState.isRequired}
              onShowHelpTextChange={setShowHelpText}
              onShowInCreationChange={setShowInCreation}
              onRequiredChange={setIsRequired}
              onOptionsManagerChange={handleOptionsManagerChange}
              onFormValid={handleFormValid}
              onSubmit={async () => {}}
              formRef={formRef}
              onSetValue={setFormSetValue}
              isEditMode={true}
              isWorkflowField
              bodyClassName="p-5 w-full"
            />

            {showAutofill ? (
              <div className="px-5 mt-[-24px] pt-0 w-full">
                <div className="w-full border-t-[0.5px] border-border-border1 my-6" />
                {/* Remount per field: the section's question id, instructions and refs are
                    all per-field state, and the drawer instance survives field switches. */}
                <AIAutofillSection
                  key={fieldData?.id}
                  fieldName={currentLabel}
                  fieldType={
                    formState.formData.fieldType ||
                    fieldData?.config?.type ||
                    fieldData?.displayType ||
                    ""
                  }
                  helpText={formState.formData.fieldHelpText || ""}
                  questionnaireId={questionnaireId || ""}
                  enabled={filledByDreamer}
                  onEnabledChange={setFilledByDreamer}
                  onInstructionsChange={setAutofillInstructions}
                  onQuestionIdChange={onAutofillQuestionIdChange}
                  onDreamerFlowChange={setDreamerFlow}
                  onSaveBlockedChange={setAutofillSaveBlocked}
                  promptOwner={promptOwner}
                  translationPrefix={AUTOFILL_TRANSLATION_KEY}
                  initialQuestionId={fieldData?.id}
                  initialFieldName={fieldData?.label}
                  isExistingAutofill={fieldData?.filledByDreamer ?? false}
                  initialDreamerFlow={fieldData?.dreamerFlow ?? null}
                  className=""
                />
                <div className="w-full border-t-[0.5px] border-border-border1 my-6" />
              </div>
            ) : null}

            <div className="p-5 w-full">
              <WorkflowRequiredFieldSection
                isRequiredForPass={isRequiredForPass}
                onRequiredForPassChange={setIsRequiredForPass}
                translationPrefix={
                  "settings.deals.workflow.requiredFieldSection"
                }
              />
            </div>
          </div>

          <FieldFormFooter
            isFormValid={isFormValid && hasEdits && !autofillSaveBlocked}
            isSubmitting={isSubmitting}
            onSubmit={handleSave}
            onCancel={handleClose}
            onDelete={handleDeleteClick}
            isEditMode={true}
          />
        </AppDrawerContent>
      </Drawer>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        title={t(`${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.title`)}
        confirmationText={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.enhancedConfirmationText`
        )}
        confirmationTextVariant="body"
        handleSubmit={handleConfirmDelete}
        handleCancel={handleCancelDelete}
        submitButtonText={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.submitButton`
        )}
        cancelButtonText={t("cancel")}
        checkboxLabel={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.checkboxLabel`
        )}
        isCheckboxRequired={true}
        onCheckboxChange={setIsDeleteConfirmed}
        isCheckboxChecked={isDeleteConfirmed}
      />
    </>
  );
};
